import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Eye, MoreHorizontal, Printer } from 'lucide-react';
import type { FacultyMember, Status } from '../../types/faculty';
import { formatFacultyStatus } from '../../types/faculty';
import { openTextPdfInNewTab } from '../../../../utils/pdf';

interface FacultyTableProps {
  data: FacultyMember[];
  isInitialLoading?: boolean;
  onSelectFaculty: (id: string) => void;
  selectedRows: string[];
  onSelectRow: (id: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
}

type SortField = 'name' | 'position' | 'status';
type SortDirection = 'asc' | 'desc';

const statusTone = (status: Status) => {
  if (status === 'active') return { dot: 'bg-aquamarine', text: 'text-jungle' };
  if (status.startsWith('on_leave')) return { dot: 'bg-coral', text: 'text-iron-grey' };
  if (status === 'inactive' || status === 'terminated') {
    return { dot: 'bg-text-tertiary', text: 'text-text-secondary' };
  }
  return { dot: 'bg-primary', text: 'text-iron-grey' };
};

const teachingSummary = (faculty: FacultyMember) => ({
  subjects: faculty.teachingAssignment?.subjects.join(', ') || '—',
  grades: faculty.teachingAssignment?.grades.join(', ') || '—',
});

export function FacultyTable({
  data,
  isInitialLoading = false,
  onSelectFaculty,
  selectedRows,
  onSelectRow,
  onSelectAll,
}: FacultyTableProps) {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [openActionsFacultyId, setOpenActionsFacultyId] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  const sortedData = useMemo(() => {
    const sorted = [...data];
    sorted.sort((left, right) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = left.lastName.localeCompare(right.lastName) || left.firstName.localeCompare(right.firstName);
      } else if (sortField === 'position') {
        comparison = left.position.localeCompare(right.position);
      } else {
        comparison = formatFacultyStatus(left.status).localeCompare(formatFacultyStatus(right.status));
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [data, sortDirection, sortField]);

  const allSelected = sortedData.length > 0 && selectedRows.length === sortedData.length;
  const someSelected = selectedRows.length > 0 && selectedRows.length < sortedData.length;

  const handlePrintProfile = (faculty: FacultyMember) => {
    void openTextPdfInNewTab({
      title: `${faculty.firstName} ${faculty.lastName}`,
      fileName: `${faculty.firstName}-${faculty.lastName}-profile.pdf`,
      lines: [
        `Position: ${faculty.position}`,
        `Employment type: ${faculty.employmentType}`,
        `Status: ${formatFacultyStatus(faculty.status)}`,
        `Divisions: ${faculty.divisions.join(', ') || '—'}`,
        `Grades: ${faculty.teachingAssignment?.grades.join(', ') || '—'}`,
        `Subjects: ${faculty.teachingAssignment?.subjects.join(', ') || '—'}`,
        `Email: ${faculty.workEmail || '—'}`,
        `Phone: ${faculty.cellPhone || '—'}`,
      ],
    });
  };

  const skeletonRows = Array.from({ length: 6 }, (_, index) => `faculty-skeleton-row-${index + 1}`);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table data-testid="faculty-table" className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              <th scope="col" className="w-12 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                <input
                  data-testid="select-all"
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer rounded border-border-strong"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={(event) => onSelectAll(event.target.checked)}
                />
              </th>
              <th
                scope="col"
                className="cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary transition-colors hover:bg-border/50"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Faculty member
                  {sortField === 'name' ? (
                    sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  ) : null}
                </div>
              </th>
              <th
                scope="col"
                className="cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary transition-colors hover:bg-border/50"
                onClick={() => handleSort('position')}
              >
                <div className="flex items-center gap-1">
                  Position
                  {sortField === 'position' ? (
                    sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  ) : null}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                Division(s)
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                Grades/subjects
              </th>
              <th
                scope="col"
                className="cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary transition-colors hover:bg-border/50"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">
                  Status
                  {sortField === 'status' ? (
                    sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  ) : null}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                Tags
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-white">
            {isInitialLoading ? (
              skeletonRows.map((rowId) => (
                <tr key={rowId} data-testid="faculty-table-loading-row" className="animate-pulse">
                  <td className="px-6 py-4">
                    <div className="h-4 w-4 rounded border border-border bg-muted/70" />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-muted/70" />
                      <div className="ml-4 space-y-2">
                        <div className="h-3 w-28 rounded bg-muted/70" />
                        <div className="h-3 w-20 rounded bg-muted/50" />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="h-3 w-20 rounded bg-muted/70" />
                      <div className="h-3 w-16 rounded bg-muted/50" />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-3 w-24 rounded bg-muted/60" />
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="h-3 w-24 rounded bg-muted/70" />
                      <div className="h-3 w-20 rounded bg-muted/50" />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-5 w-20 rounded-full bg-muted/60" />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1">
                      <div className="h-5 w-12 rounded-full bg-muted/60" />
                      <div className="h-5 w-10 rounded-full bg-muted/50" />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="ml-auto h-8 w-8 rounded-full bg-muted/60" />
                  </td>
                </tr>
              ))
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-text-secondary">
                  Adjust the filters to surface faculty records.
                </td>
              </tr>
            ) : (
              sortedData.map((faculty) => {
                const isSelected = selectedRows.includes(faculty.id);
                const statusStyle = statusTone(faculty.status);
                const summary = teachingSummary(faculty);
                return (
                  <tr
                    key={faculty.id}
                    data-testid="faculty-row"
                    className={`cursor-pointer transition-colors hover:bg-muted/50 ${isSelected ? 'bg-muted/30' : ''}`}
                    onClick={() => onSelectFaculty(faculty.id)}
                  >
                    <td
                      className="whitespace-nowrap px-6 py-4"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <input
                        data-testid="row-checkbox"
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer rounded border-border-strong"
                        checked={isSelected}
                        onChange={(event) => onSelectRow(faculty.id, event.target.checked)}
                      />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {faculty.profilePhoto ? (
                            <img
                              className="h-10 w-10 rounded-full border border-border object-cover"
                              src={faculty.profilePhoto}
                              alt=""
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-muted font-medium text-text-secondary">
                              {faculty.firstName[0]}
                              {faculty.lastName[0]}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-iron-grey">
                            {faculty.firstName} {faculty.lastName}
                          </div>
                          <div className="text-sm text-text-secondary">
                            {[faculty.hebrewFirstName, faculty.hebrewLastName].filter(Boolean).join(' ')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-iron-grey">{faculty.position}</div>
                      <div className="text-xs text-text-secondary">{faculty.employmentType}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-iron-grey">{faculty.divisions.join(', ') || '—'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-[200px] truncate text-sm text-iron-grey" title={summary.subjects}>
                        {summary.subjects}
                      </div>
                      <div className="max-w-[200px] truncate text-xs text-text-secondary" title={summary.grades}>
                        {summary.grades}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${statusStyle.dot}`} />
                        <span className={`text-sm ${statusStyle.text}`}>{formatFacultyStatus(faculty.status)}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex max-w-[150px] flex-wrap gap-1">
                        {faculty.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-text-secondary"
                          >
                            {tag}
                          </span>
                        ))}
                        {faculty.tags.length > 2 ? (
                          <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-text-secondary">
                            +{faculty.tags.length - 2}
                          </span>
                        ) : null}
                        {faculty.tags.length === 0 ? <span className="text-sm text-text-tertiary">—</span> : null}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      <div className="relative flex items-center justify-end gap-2">
                        <button
                          type="button"
                          data-testid="actions-menu"
                          aria-label={`More actions for ${faculty.firstName} ${faculty.lastName}`}
                          className={`rounded-full p-1 text-irongrey/40 transition-colors hover:bg-gray-100 hover:text-primary ${
                            openActionsFacultyId === faculty.id ? 'bg-primary/5 text-primary' : ''
                          }`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setOpenActionsFacultyId((current) => current === faculty.id ? null : faculty.id);
                          }}
                          title="More actions"
                        >
                          <MoreHorizontal className="h-5 w-5" />
                        </button>
                        {openActionsFacultyId === faculty.id ? (
                          <div
                            className="absolute right-0 top-full z-50 mt-2 w-48 rounded-xl border border-gray-100 bg-white py-1.5 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)]"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <button
                              type="button"
                              data-testid="actions-view"
                              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-xs font-bold text-irongrey transition-colors hover:bg-gray-50"
                              onClick={(event) => {
                                event.stopPropagation();
                                setOpenActionsFacultyId(null);
                                onSelectFaculty(faculty.id);
                              }}
                            >
                              <Eye size={14} className="text-irongrey/60" />
                              View
                            </button>
                            <button
                              type="button"
                              data-testid="actions-print-profile"
                              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-xs font-bold text-irongrey transition-colors hover:bg-gray-50"
                              onClick={(event) => {
                                event.stopPropagation();
                                setOpenActionsFacultyId(null);
                                handlePrintProfile(faculty);
                              }}
                            >
                              <Printer size={14} className="text-irongrey/60" />
                              Print Profile
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t border-border bg-white px-4 py-3 sm:px-6">
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-text-secondary">
              Showing <span className="font-medium">{sortedData.length > 0 ? 1 : 0}</span> to{' '}
              <span className="font-medium">{sortedData.length}</span> of{' '}
              <span className="font-medium">{sortedData.length}</span> results
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                type="button"
                className="relative inline-flex items-center rounded-l-md border border-border bg-white px-2 py-2 text-sm font-medium text-text-secondary disabled:cursor-not-allowed disabled:opacity-50 hover:bg-muted"
                disabled
              >
                Previous
              </button>
              <button type="button" className="relative inline-flex items-center border border-border bg-primary px-4 py-2 text-sm font-medium text-white">
                1
              </button>
              <button
                type="button"
                className="relative inline-flex items-center rounded-r-md border border-border bg-white px-2 py-2 text-sm font-medium text-text-secondary disabled:cursor-not-allowed disabled:opacity-50 hover:bg-muted"
                disabled
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
